import { z } from 'zod';
import { type PrintTemplateDocument, printTemplateDocumentSchema } from './print-template';

/**
 * The built-in template — NDC's letterhead, expressed as data.
 *
 * Every literal on the sheet sits in a row of `print_templates`: the two
 * technologists and their qualifications, the lab's phone numbers and address,
 * the red-then-blue rule, the "NOT VALID FOR COURT" strip. Changing any of
 * them is an edit on a screen, not a deploy.
 *
 * Written as a *partial* and parsed rather than spelled out in full, so it
 * cannot drift from the schema: every field it omits takes the schema's
 * default, and a field added to the schema tomorrow needs no edit here.
 */
const DEFAULT_INPUT: z.input<typeof printTemplateDocumentSchema> = {
  page: {
    size: 'A4',
    orientation: 'portrait',
    margin_top: 5,
    margin_right: 8,
    margin_bottom: 4,
    margin_left: 8,
  },

  theme: {
    font: 'Helvetica',
    base_size: 9,
    // These two are the printed brand: they are also step 9 of the indigo and
    // crimson ramps in client/styles/theme/primitives.css, so screen and paper
    // agree. Changing one without the other is what makes a preview lie.
    text_color: '#2d358a',
    accent_color: '#2d358a',
    accent_alt_color: '#ba1c37',
    muted_color: '#5b6178',
  },

  header: {
    enabled: true,
    repeat: 'every',
    layout: 'compact',
    band: 38,
    logo: { enabled: true, data_url: null, width: 30, height: 26 },
    barcode: { enabled: true, source: 'pin_no', height: 11, module_width: 0.33, show_value: true },
    lab_name: '',
    lab_lines: [],
    rule: true,
    rule_accent_width: 30,
    patient_columns: 2,
    // Column-major order: the first four fill the left column, the rest the
    // right — which is how the lab's letterhead has always read.
    patient_fields: [
      { key: 'name', label: 'Name' },
      { key: 'age', label: 'Age' },
      { key: 'visit_date', label: 'Visit date' },
      { key: 'reported_at', label: 'Printed' },
      { key: 'gender', label: 'Gender' },
      { key: 'refer_by', label: 'Referred by' },
      { key: 'pin_no', label: 'PIN No' },
      { key: 'mr_no', label: 'MR No' },
      { key: 'phone_number', label: 'Phone', enabled: false },
      { key: 'payment_status', label: 'Payment', enabled: false },
    ],
  },

  body: {
    title_enabled: true,
    title_align: 'center',
    title_size: 15,
    grid: 'horizontal',
    row_padding: 2.5,
    flag_abnormal: true,
    bold_abnormal: true,
    remarks: { enabled: true },
    comments: { enabled: true, title: 'Comments' },
    department_heading: true,
    test_flow: 'new_page',
  },

  footer: {
    enabled: true,
    repeat: 'every',
    band: 36,
    rule: true,
    rule_accent_width: 30,
    columns: [
      {
        heading: 'CONTACT',
        lines: [
          '03239256330, 03554972343',
          'info.ndcskd@gmail.com',
          'Faiz Market Opposite RHQ Hospital Skd',
        ],
      },
    ],
    page_number: { enabled: true, format: 'Page {page} of {pages}', align: 'right' },
    strip: {
      enabled: true,
      text: 'NOT VALID FOR COURT',
      note: "THIS IS A DIGITALLY VERIFIED REPORT AND DOESN'T REQUIRE A MANUAL SIGNATURE",
      background: '#ba1c37',
      foreground: '#ffffff',
    },
  },

  watermark: { enabled: true, text: 'NDC', color: '#2d358a', opacity: 0.06, angle: -30 },
};

export const DEFAULT_PRINT_TEMPLATE_NAME = 'NDC letterhead';

export const DEFAULT_PRINT_TEMPLATE_DESCRIPTION =
  'The lab letterhead: logo, patient barcode, two-tone rule, contact details and the verification strip.';

/**
 * A function rather than a constant: parsing returns a fresh object tree every
 * call, so a caller that adjusts the document it was handed — which the print
 * endpoint does, to apply per-job overrides — cannot alter the defaults every
 * later job in the process starts from.
 */
export function defaultPrintTemplateDocument(): PrintTemplateDocument {
  return printTemplateDocumentSchema.parse(DEFAULT_INPUT);
}
